import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { formatPrice, formatHour } from "@/lib/utils";
import { getImageUrl } from "@/lib/images";
import { Calendar, Clock, MapPin, ShieldCheck, CheckCircle, Navigation } from "lucide-react";
import BackButton from "@/components/BackButton";

const STATUS_STYLES = {
  confirmed: "border-volt text-volt",
  pending: "border-yellow-400 text-yellow-400",
  cancelled: "border-border/30 text-muted-foreground/80",
  rejected: "border-red-400 text-red-400",
  checked_in: "border-sky-400 text-sky-400",
  completed: "border-emerald-400 text-emerald-400",
};

function displayStatus(status, paymentStatus) {
  if (status === "checked_in") return "checked_in";
  if (status === "confirmed" && (paymentStatus === "mocked_paid" || paymentStatus === "paid_at_court")) return "completed";
  return status;
}

export default function SharedBooking() {
  const { t } = useTranslation();
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    api
      .get(`/bookings/${bookingId}/public`)
      .then((r) => setBooking(r.data))
      .catch(() => {
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [bookingId]);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-background flex items-center justify-center">
        <div className="label-eyebrow animate-pulse">{t("common.loading")}</div>
      </div>
    );
  }

  if (loadError || !booking) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-background">
        <div className="max-w-xl mx-auto px-5 md:px-10 py-10">
          <BackButton className="mb-8" />
          <div className="border border-border bg-card p-8 text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-muted mb-4">
              <Calendar className="h-8 w-8 text-muted-foreground/60" />
            </div>
            <h1 className="font-heading text-3xl tracking-tight mb-2">{t("shared_booking.not_found_title")}</h1>
            <p className="text-sm text-muted-foreground/80 mb-6">{t("shared_booking.not_found_hint")}</p>
            <Link to="/search" className="btn-volt rounded-sm px-8 py-3 text-xs inline-flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" /> {t("bookings.find_court")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const statusKey = displayStatus(booking.status, booking.payment_status);
  const court = booking.court;
  const mapDestination = court?.lat && court?.lng
    ? `${court.lat},${court.lng}`
    : [court?.address, court?.city].filter(Boolean).join(", ");
  const directionsHref = mapDestination ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapDestination)}` : null;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-xl mx-auto px-5 md:px-10 py-10">
        <BackButton className="mb-8" />

        {booking.court_image && (
          <div className="aspect-[16/9] overflow-hidden border border-border mb-6 relative">
            <img src={getImageUrl(booking.court_image, 'large')} alt={booking.court_name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
          </div>
        )}

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-volt/10 mb-4">
            <CheckCircle className="h-8 w-8 text-volt" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight mb-2">{booking.court_name}</h1>
          <div className="flex items-center gap-2 mt-2 mb-6">
            {booking.court_sport && (
              <span className="text-[9px] tracking-widest uppercase border border-border px-1.5 py-0.5 text-muted-foreground/80">
                {t("home.sports." + booking.court_sport)}
              </span>
            )}
            <span className={`text-[9px] tracking-widest uppercase border px-1.5 py-0.5 ${STATUS_STYLES[statusKey] || "border-border/30 text-muted-foreground/80"}`}>
              {t(`bookings.status_${statusKey}`, statusKey)}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="border border-border/30 bg-card p-4">
            <Calendar className="h-4 w-4 text-volt mb-1" />
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-0.5">{t("booking_details.date")}</div>
            <div className="text-sm font-semibold">{booking.date}</div>
          </div>
          <div className="border border-border/30 bg-card p-4">
            <Clock className="h-4 w-4 text-volt mb-1" />
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-0.5">{t("booking_details.time")}</div>
            <div className="text-sm font-semibold">{formatHour(booking.start_hour)} – {formatHour(booking.end_hour)}</div>
          </div>
          <div className="border border-border/30 bg-card p-4">
            <MapPin className="h-4 w-4 text-volt mb-1" />
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-0.5">{t("booking_details.total")}</div>
            <div className="font-heading text-2xl text-volt">{formatPrice(booking.total_price, booking.currency)}</div>
          </div>
          <div className="border border-border/30 bg-card p-4">
            <CheckCircle className="h-4 w-4 text-volt mb-1" />
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-0.5">{t("booking_details.status")}</div>
            <div className={`font-semibold text-sm ${statusKey === "completed" ? "text-emerald-400" : statusKey === "pending" ? "text-yellow-400" : statusKey === "rejected" || statusKey === "cancelled" ? "text-red-400" : "text-volt"}`}>
              {t(`bookings.status_${statusKey}`, statusKey)}
            </div>
          </div>
        </div>

        <div className="border border-border bg-card p-6 mb-6">

          {booking.selected_extras && booking.selected_extras.length > 0 && (
            <div className="mt-4 border border-border/30 bg-background p-4">
              <div className="label-eyebrow mb-3">{t("court.extras")}</div>
              <div className="space-y-2">
                {booking.selected_extras.map((extra, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{extra.name}{extra.qty > 1 ? ` x${extra.qty}` : ""}</span>
                    {extra.price > 0 && <span className="text-volt">+{formatPrice(extra.price * (extra.qty || 1), booking.currency)}</span>}
                  </div>
                ))}
                {booking.extras_total > 0 && (
                  <div className="flex justify-between text-sm border-t border-border pt-2 mt-2">
                    <span className="text-muted-foreground/80 text-[10px] tracking-widest uppercase">{t("booking_details.extras_total")}</span>
                    <span className="text-foreground font-bold">{formatPrice(booking.extras_total, booking.currency)}</span>
                  </div>
                )}
                {booking.discount_label && booking.discount_amount > 0 && (
                  <div className="flex justify-between text-sm border-t border-border pt-2 mt-2">
                    <span className="text-emerald-400">{booking.discount_label}</span>
                    <span className="text-emerald-400">&minus;{formatPrice(booking.discount_amount, booking.currency)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {court && (
            <div className="mt-4 border border-border/30 bg-background p-4">
              <div className="label-eyebrow mb-3">{t("booking_details.court_info")}</div>
              <div className="space-y-2 text-sm">
                {court.sport && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <ShieldCheck className="h-4 w-4 text-muted-foreground/80" />
                    {t(`home.sports.${court.sport}`)}
                  </div>
                )}
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4 text-muted-foreground/80" />
                  {court.address}, {court.city}
                </div>
                {court.indoor !== undefined && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <ShieldCheck className="h-4 w-4 text-muted-foreground/80" />
                    {court.indoor ? t("court.indoor") : t("court.outdoor")}
                  </div>
                )}
              </div>
              {directionsHref && (
                <a
                  href={directionsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline rounded-sm mt-3 px-3 py-1.5 text-xs flex items-center justify-center gap-2 w-fit"
                >
                  <Navigation className="h-3.5 w-3.5" /> {t("court.navigate")}
                </a>
              )}
            </div>
          )}

          {/* Host contact */}
          {(booking.host_whatsapp || booking.host_phone) && (() => {
            const canShow = booking.host_contact_visibility === "always" || ["confirmed", "checked_in", "completed"].includes(booking.status);
            if (!canShow) return null;
            return (
              <div className="mt-4 border border-border/30 bg-background p-4">
                <div className="label-eyebrow mb-3">{t("booking_details.host_contact")}</div>
                <div className="space-y-2">
                  {booking.host_whatsapp && (
                    <a
                      href={`https://wa.me/${booking.host_whatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-volt transition-colors"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </a>
                  )}
                  {booking.host_phone && (
                    <a
                      href={`tel:${booking.host_phone}`}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-volt transition-colors"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                      </svg>
                      {booking.host_phone}
                    </a>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        <div className="text-center space-y-3 mb-8">
          <Link
            to={`/courts/${booking.court_id}`}
            className="btn-volt rounded-sm px-8 py-3 text-xs inline-flex items-center gap-2"
          >
            <Calendar className="h-3.5 w-3.5" /> {t("booking_details.view_court")}
          </Link>
          <p className="text-[10px] text-muted-foreground/60 tracking-wider">
            {t("shared_booking.sign_in_hint")}
          </p>
        </div>

        <div className="border border-volt/10 bg-volt/5 p-6 text-center mt-8">
          <p className="font-heading text-xl text-volt mb-2">Book courts in seconds</p>
          <p className="text-sm text-muted-foreground/80 mb-4">Join Go<span className="text-volt">Captain</span> to find and book the best sports courts near you.</p>
          <a href="/register" className="btn-volt inline-block px-6 py-3 text-xs">Create Free Account</a>
        </div>
      </div>
    </div>
  );
}
